import { ArrowRight, Check, ShieldCheck, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT } from "../../data/publicSite";
import usePageMeta from "../../lib/usePageMeta";
import AccessModelCallout from "../../components/public/AccessModelCallout";

export default function ForOrganizationsPage() {
  usePageMeta({
    title: "For Organizations | California Corporate Chaplains",
    description: "Confidential, exploratory conversations for California employers considering workplace chaplaincy support — never a pitch. Scheduled onsite chaplaincy in Sacramento, Placer, El Dorado, and Nevada Counties, plus remote support across California and nationwide.",
  });
  return (
    <div data-testid="for-organizations-page">
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20 pb-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">For organizations</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
                A quiet first conversation about workplace support.
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Tell us a little about your team. We'll follow up to learn more about your organization and what kind of support may fit — exploratory, never a pitch.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div
                className="aspect-[4/3] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="for-organizations-hero-image"
              >
                <img
                  src="/images/for-organizations/hero.jpg"
                  alt="A diverse group of business professionals in a bright, modern workplace reviewing organizational reports and a laptop together during a working meeting."
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <AccessModelCallout
            variant="default"
            className="mb-12"
            testId="forg-access-model-callout"
          />

          <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <h2 className="font-heading text-2xl tracking-tight text-slate-900">What to expect</h2>
            <ul className="mt-6 space-y-4 text-slate-700">
              {[
                "A 15-minute initial conversation with a CCCHAPS team lead, with the option for a longer follow-up meeting",
                "Clarity on how chaplaincy fits alongside your existing benefits or EAP",
                "Options for scheduled on-site, remote, or blended chaplain support — shaped around your workforce and locations, with 24/7 on-call chaplain access built in",
                "Care arrangements for healthcare, hospice, senior living, board and care, and general workforce settings",
                "Transparent scope, cadence, and pricing — no hidden bundling",
                "Care Portal demonstration on request",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={2} />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 md:p-8">
              <h3 className="font-heading text-lg font-medium text-slate-900">Organizational services</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Care arrangements vary by organization type, workforce needs, locations, and desired level of chaplain presence. Common arrangements may include:
              </p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2.5 text-sm text-slate-700">
                {[
                  "Employer-sponsored workplace chaplaincy",
                  "Healthcare & hospice team support programs",
                  "Senior living / board & care visitation programs",
                  "Enhanced crisis & organizational response arrangements",
                  "Training & workshop packages",
                  "Executive & leadership 1:1 programs",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-[#4A6FA5] shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                Scope, cadence, and pricing are discussed during the exploratory conversation and tailored to the organization's care arrangement.
              </p>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="for-orgs-founder-crisis-experience">
              <h3 className="font-heading text-lg font-medium text-slate-900">Experienced Support When It Matters Most</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                Founded by Chaplain Derrick Hall, California Corporate Chaplains draws on five years of law enforcement
                chaplaincy experience across 14 agencies, including specialized training in crisis intervention, rapid
                response, critical incidents, and active-shooter situations.
              </p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                This experience strengthens our ability to support employees and organizational leadership through
                workplace crises, unexpected loss, and challenging circumstances with confidential, compassionate,
                non-clinical care.
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-7 sticky top-24">
              <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">Request a consultation</p>
              <h3 className="font-heading text-xl font-medium text-slate-900">Let's have a quiet first conversation.</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Begin with a 15-minute initial conversation. We can schedule a longer follow-up meeting if it's useful.
              </p>
              <Link
                to="/schedule?type=organizational_consultation&duration=15"
                data-testid="consultation-mailto"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
              >
                Schedule a 15-Minute Business Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/schedule?type=organizational_consultation&intent=proposal"
                data-testid="for-orgs-proposal-cta"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white border border-[#4A6FA5] text-[#4A6FA5] font-medium hover:bg-[#F0F4F8]"
              >
                Request a Workforce Care Proposal <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/packages"
                data-testid="for-orgs-packages-link"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
              >
                Explore Care Team Packages <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
              >
                Other contact options <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="mt-5 flex items-start gap-2 text-xs text-slate-500 leading-relaxed">
                <ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5] shrink-0 mt-0.5" />
                Our workplace chaplaincy services are voluntary, confidential within applicable ethical and legal limits,
                non-clinical, and respectful of employees of all faith traditions and those with no religious affiliation.
                Proposals are tailored to each organization's needs.
              </p>
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* Where We Serve */}
      <section
        className="py-14 md:py-20 bg-[#F8FAFC] border-t border-slate-200"
        data-testid="orgs-page-where-we-serve"
        aria-labelledby="orgs-page-where-we-serve-heading"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Service area</p>
          <h2
            id="orgs-page-where-we-serve-heading"
            className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900"
          >
            Where We Serve
          </h2>
          <p className="mt-4 text-slate-700 leading-relaxed max-w-3xl">
            CCCHAPS currently provides scheduled onsite chaplaincy throughout
            Sacramento, Placer, El Dorado, and Nevada Counties. Remote chaplain
            support is available to organizations and employees throughout
            California and nationwide, based on service arrangement and
            availability.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">Onsite chaplaincy</p>
              <p className="mt-2 text-sm font-medium text-slate-900">Sacramento · Placer · El Dorado · Nevada Counties</p>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">Scheduled workplace presence based on your organization's care arrangement.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">Remote chaplain support</p>
              <p className="mt-2 text-sm font-medium text-slate-900">California &amp; nationwide</p>
              <p className="mt-2 text-xs text-slate-500 leading-relaxed">Phone, text, video, and confidential-message channels — with 24/7 on-call chaplain access.</p>
            </div>
          </div>
          <p className="mt-6 text-xs text-slate-500 leading-relaxed max-w-3xl">
            Organizations outside the four-county onsite area can still partner with CCCHAPS through remote services. 24/7 refers to on-call chaplain availability, not continuous onsite presence.
          </p>
        </div>
      </section>
      <section
        className="py-16 md:py-24 bg-white border-t border-slate-200"
        data-testid="orgs-page-video-section"
        aria-labelledby="orgs-page-video-heading"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
              See CCCHAPS in action
            </p>
            <h2
              id="orgs-page-video-heading"
              className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900"
            >
              What Workplace Chaplaincy Looks Like
            </h2>
            <p className="mt-4 text-slate-700 leading-relaxed text-base sm:text-lg">
              A brief look at how CCCHAPS supports employees and organizations
              through personal challenges, grief, stress, crisis, and the
              everyday realities of work and life.
            </p>
          </div>

          <div
            className="mt-10 rounded-2xl overflow-hidden border border-slate-200 bg-slate-900 shadow-sm aspect-video"
            data-testid="orgs-page-video-player-wrap"
          >
            <video
              data-testid="orgs-page-video-player"
              className="w-full h-full block"
              controls
              preload="metadata"
              playsInline
              title="CCCHAPS workplace chaplaincy overview video"
              aria-label="CCCHAPS workplace chaplaincy overview video"
            >
              <source
                src="/videos/ccchaps-workplace-chaplaincy-overview.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag. You can{" "}
              <a
                href="/videos/ccchaps-workplace-chaplaincy-overview.mp4"
                className="underline text-white"
              >
                download the CCCHAPS workplace chaplaincy overview video
              </a>{" "}
              instead.
            </video>
          </div>
        </div>
      </section>

      {/* Organizations We've Supported — trust / credibility */}
      <section
        id="organizations-supported"
        className="scroll-mt-20 lg:scroll-mt-24 py-16 md:py-24 bg-[#F8FAFC] border-t border-slate-200"
        data-testid="orgs-page-orgs-supported"
        aria-labelledby="orgs-page-orgs-supported-heading"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
              Trust & credibility
            </p>
            <h2
              id="orgs-page-orgs-supported-heading"
              className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900"
            >
              Organizations We've Supported
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Trusted by organizations seeking compassionate, professional workplace chaplaincy and crisis support.
            </p>
          </div>

          <ul
            className="mt-10 md:mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
            data-testid="orgs-supported-grid"
          >
            {[
              { name: "Honda", slug: "honda", file: "honda.svg", padding: "p-6 md:p-8" },
              { name: "Wendy's", slug: "wendys", file: "wendys.svg", padding: "p-5 md:p-6" },
              { name: "McDonald's", slug: "mcdonalds", file: "mcdonalds.svg", padding: "p-6 md:p-7" },
              { name: "Jiffy Lube", slug: "jiffy-lube", file: "jiffy-lube.png", padding: "p-5 md:p-6" },
              { name: "Lexus", slug: "lexus", file: "lexus.svg", padding: "p-6 md:p-7" },
              { name: "Toyota", slug: "toyota", file: "toyota.svg", padding: "p-6 md:p-7" },
              { name: "The UPS Store", slug: "the-ups-store", file: "the-ups-store.svg", padding: "p-5 md:p-6" },
              { name: "Eskaton Senior Living", slug: "eskaton-senior-living", file: "eskaton.svg", padding: "p-5 md:p-6" },
            ].map((o) => (
              <li
                key={o.slug}
                data-testid={`org-logo-${o.slug}`}
                className="aspect-[3/2] flex items-center justify-center bg-white border border-slate-200 rounded-xl overflow-hidden"
              >
                <img
                  src={`/logos/organizations/${o.file}`}
                  alt={`${o.name} logo`}
                  loading="lazy"
                  draggable="false"
                  className={`max-h-full max-w-full w-auto h-auto object-contain select-none pointer-events-none ${o.padding}`}
                />
              </li>
            ))}
          </ul>

          <p
            className="mt-8 md:mt-10 max-w-3xl text-xs text-slate-500 leading-relaxed"
            data-testid="orgs-supported-disclaimer"
          >
            Logos shown represent organizations where chaplaincy, crisis response, or workplace support services were provided. Display does not imply an ongoing contract, endorsement, sponsorship, or partnership.
          </p>
        </div>
      </section>

      {/* EAP comparison */}
      <section id="chaplaincy-vs-eap" className="scroll-mt-20 lg:scroll-mt-24 py-16 md:py-24 bg-white border-t border-slate-200" data-testid="orgs-page-eap-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Chaplaincy vs. EAP</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
              Different roles. Better together.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Employee Assistance Programs and workplace chaplaincy are two different approaches to supporting employees. An EAP typically provides short-term clinical counseling, assessment, referrals, and other professional resources. CCCHAPS provides ongoing, non-clinical, relationship-based care that meets employees where they are — at work, virtually, during personal crises, and through significant life events.
            </p>
            <p className="mt-3 text-slate-700 font-medium">The strongest workforce-care strategies may include both.</p>
          </div>

          {(() => {
            const rows = [
              ["Ongoing employee relationship", "Consistent, relationship-based chaplain support", "Typically short-term or episodic support"],
              ["Regular workplace presence", "Scheduled on-site presence available", "Varies by provider and employer plan"],
              ["Remote employee support", "24/7 on-call access through configured phone, text, confidential-message, and available remote-support channels (channels vary by organization)", "Commonly phone, video, web, or app-based"],
              ["Stress & emotional support", "Non-clinical emotional and relational support", "Short-term counseling and related services"],
              ["Workplace conflict support", "Relational support, listening, de-escalation, and appropriate referral", "Conflict resolution or consultation may be available"],
              ["Leadership & supervisor support", "Ongoing confidential support and consultation", "Management consultation is often available"],
              ["Workplace connection & organizational care", "Relationship-based chaplain presence designed to support connection, care, and access to confidential human support", "Organizational consultation and related resources may be available"],
              ["Employee education & personal-development resources", "Resources, training, workshops, and supportive materials", "Often available depending on the EAP"],
              ["Layoff, restructuring & transition support", "Personal support for affected employees and organizational leaders", "Transition and organizational-change support may be available"],
              ["Hospital & extended-care visitation", "Available when included in the service arrangement", "Generally counseling/referral based; in-person visitation varies"],
              ["Serious illness support", "Employee and family support through serious illness", "Short-term counseling, assessment, and referral"],
              ["Death, dying & anticipatory grief", "Relational support for employees and families before and after a death", "Grief counseling and referral may be available"],
              ["Workplace grief & critical incidents", "On-site or remote support when operationally available", "Crisis and critical-incident services are often available"],
              ["Funeral & memorial support", "Assistance with memorials, funerals, and employee remembrance when requested", "Generally outside the traditional core EAP role"],
              ["Ongoing grief support", "Short- and longer-term relational grief support", "Usually short-term counseling with referral when longer-term care is needed"],
              ["Family & household concerns", "Supportive listening, presence, and appropriate referrals", "Counseling and referrals for family concerns are commonly available"],
              ["Substance-use concerns", "Non-clinical support, encouragement, and referral to appropriate professional resources", "Assessment, short-term counseling, treatment referral, and related resources"],
              ["Psychiatric/clinical treatment", "No — CCCHAPS does not replace licensed mental-health treatment", "Clinical services or referrals may be available"],
              ["Ethical, values & meaning-centered support", "Confidential, belief-respectful conversation without imposing a religious agenda", "May be addressed clinically or through other EAP resources"],
              ["Community-resource referrals", "Yes", "Yes"],
              ["Organizational reporting", "Aggregate, non-identifying utilization reporting only", "Utilization and aggregate reporting varies by provider"],
            ];
            return (
              <>
                {/* Desktop table (md+) */}
                <div className="hidden md:block overflow-hidden rounded-2xl border border-slate-200" data-testid="eap-table-desktop">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F8FAFC]">
                      <tr>
                        <th className="text-left px-5 py-4 font-semibold text-slate-600 text-xs uppercase tracking-[0.16em] w-1/4">Support Area</th>
                        <th className="text-left px-5 py-4 font-semibold text-[#4A6FA5] text-xs uppercase tracking-[0.16em]">CCCHAPS Workplace Chaplaincy</th>
                        <th className="text-left px-5 py-4 font-semibold text-slate-600 text-xs uppercase tracking-[0.16em]">Traditional EAP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((row) => (
                        <tr key={row[0]} className="hover:bg-slate-50/60 align-top">
                          <td className="px-5 py-4 font-medium text-slate-900">{row[0]}</td>
                          <td className="px-5 py-4 text-slate-700 leading-relaxed">{row[1]}</td>
                          <td className="px-5 py-4 text-slate-600 leading-relaxed">{row[2]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile stacked cards (below md) */}
                <ul className="md:hidden space-y-3" data-testid="eap-cards-mobile">
                  {rows.map((row) => (
                    <li key={row[0]} className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{row[0]}</p>
                      <div className="mt-3">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#4A6FA5] font-semibold">CCCHAPS Workplace Chaplaincy</p>
                        <p className="mt-1 text-sm text-slate-800 leading-relaxed">{row[1]}</p>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Traditional EAP</p>
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{row[2]}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            );
          })()}

          <div className="mt-10 max-w-3xl" data-testid="eap-difference-presence">
            <h3 className="font-heading text-2xl tracking-tight text-slate-900">The difference is presence.</h3>
            <p className="mt-3 text-slate-700 leading-relaxed">
              An EAP gives employees access to important professional and clinical resources. CCCHAPS adds something different: a trusted human relationship that can develop before a crisis occurs and remain present through the everyday realities of work and life.
            </p>
          </div>

          <p className="mt-6 max-w-3xl text-xs text-slate-500 leading-relaxed" data-testid="eap-disclaimer">
            CCCHAPS does not replace an EAP, licensed therapist, medical provider, or emergency service. We complement existing employee benefits by adding accessible, relationship-based workforce care.
          </p>

          <div className="mt-8">
            <Link
              to="/resources/chaplaincy-vs-eap"
              data-testid="eap-cta-full-comparison"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
            >
              View the Full Chaplaincy vs. EAP Comparison <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
